import React from "react";
import { Stack, Table, Text } from "@mantine/core";
import { Alert } from "@xplatform/design-system";
import { IconAlertTriangle, IconReplace } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { Supersession } from "@models/reviews";

/**
 * Feedback after a re-upload replaced a pending upload (PBI #781340): how many verdicts
 * changed / stayed the same, and up to 5 changed rows as examples.
 */
export const SupersessionPanel: React.FC<{ supersession: Supersession }> = ({ supersession: s }) => {
  const { t } = useTranslation();
  const replaced = t("reviews.supersession.replaced", {
    by: s.replaced_uploaded_by,
    at: s.replaced_uploaded_at ? new Date(s.replaced_uploaded_at).toLocaleString() : "—",
  });

  if (s.error || s.total == null || s.changed == null || s.unchanged == null) {
    return (
      <Alert icon={<IconAlertTriangle size={16} />} color="orange" data-testid="supersession">
        <Text size="sm">{replaced}</Text>
        <Text size="sm">{t("reviews.supersession.compareFailed", { error: s.error ?? "" })}</Text>
      </Alert>
    );
  }

  const samples = s.samples ?? [];
  return (
    <Alert icon={<IconReplace size={16} />} color="blue" data-testid="supersession">
      <Stack gap="xs">
        <Text size="sm">{replaced}</Text>
        {s.changed === 0 ? (
          <Text size="sm" fw={600}>
            {t("reviews.supersession.noneChanged", { total: s.total })}
          </Text>
        ) : (
          <>
            <Text size="sm" fw={600}>
              {t("reviews.supersession.changed", { changed: s.changed, total: s.total })}
            </Text>
            <Text size="sm" fw={600}>
              {t("reviews.supersession.unchanged", { unchanged: s.unchanged, total: s.total })}
            </Text>
            <Text size="sm" mt="xs">
              {t("reviews.supersession.samplesTitle", { shown: samples.length })}
            </Text>
            <Table withTableBorder style={{ tableLayout: "fixed", width: "100%" }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: "80px" }}>RowId</Table.Th>
                  <Table.Th style={{ width: "140px" }}>ContentHash</Table.Th>
                  <Table.Th>{t("reviews.supersession.text")}</Table.Th>
                  <Table.Th style={{ width: "130px" }}>{t("reviews.supersession.beforeAfter")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {samples.map((x) => (
                  <Table.Tr key={x.row_id}>
                    <Table.Td>{x.row_id}</Table.Td>
                    <Table.Td>{x.content_hash}</Table.Td>
                    <Table.Td>{x.text || "—"}</Table.Td>
                    <Table.Td>{`${x.old || "—"} → ${x.new}`}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </>
        )}
      </Stack>
    </Alert>
  );
};
